import { Alert } from "@/components/ui/Alert";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

export type ProjectAngebotHint = {
  id: number;
  label: string;
  statusLabel: string;
};

export function TimesheetInvoiceAngebotAlert({
  angebote,
  className,
}: {
  angebote: ProjectAngebotHint[];
  className?: string;
}) {
  if (!angebote.length) return null;

  const labels = angebote.map((a) => a.label).filter(Boolean);
  const listText =
    labels.length === 1
      ? labels[0]
      : labels.length <= 3
        ? labels.join(", ")
        : `${labels.slice(0, 2).join(", ")} und ${labels.length - 2} weitere`;

  return (
    <Alert variant="warning" title="Angebot im Projekt vorhanden" className={className}>
      <p>
        Zu diesem Projekt gibt es{" "}
        {angebote.length === 1 ? "ein Angebot" : `${angebote.length} Angebote`}
        {listText ? (
          <>
            {" "}
            (<span className="font-medium">{listText}</span>)
          </>
        ) : null}
        . Bitte bei der Abrechnung berücksichtigen – Festpreis, enthaltene Leistungen und
        bereits vereinbarte Positionen prüfen.
      </p>
      {angebote.length > 1 ? (
        <ul className="mt-2 list-inside list-disc text-xs text-label-secondary">
          {angebote.slice(0, 6).map((a) => (
            <li key={a.id}>
              {a.label}
              {a.statusLabel ? ` · ${a.statusLabel}` : ""}
            </li>
          ))}
          {angebote.length > 6 ? <li>… und {angebote.length - 6} weitere</li> : null}
        </ul>
      ) : angebote[0]?.statusLabel ? (
        <p className="mt-1 text-xs text-label-secondary">Status: {angebote[0].statusLabel}</p>
      ) : null}
    </Alert>
  );
}

/** Bestätigung, wenn kein anderer Dialog den Hinweis bereits zeigt. */
export function TimesheetInvoiceAngebotHintDialog({
  open,
  busy = false,
  angebote,
  onClose,
  onContinue,
}: {
  open: boolean;
  busy?: boolean;
  angebote: ProjectAngebotHint[];
  onClose: () => void;
  onContinue: () => void;
}) {
  const layers = useTimesheetFlowLayers();
  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="Hinweis zur Abrechnung"
      size="sm"
      zIndexClass={layers.deep}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={onContinue} disabled={busy} loading={busy}>
            Verstanden, weiter
          </Button>
        </div>
      }
    >
      <TimesheetInvoiceAngebotAlert angebote={angebote} />
    </Dialog>
  );
}
